import {Visualizer, VisualizerControl, VisualizationUpdateLevel} from '../data-source/visualizer';
import {Datum} from '../data-source/types';
import {Map as TLMDMap} from '../map/map';
import {Timeline} from '../timeline/timeline';
import {Drawer} from '../panel/cabinets';
import {delegatedObserver} from '../dom/dom';
import {throttleTime, debounceTime, map} from 'rxjs/operators';
import {removeDiacritics} from '../util/util';

import {ColumnDefinition, DatumPropertyDefinition, ObservationDataSource, ObservationDatum} from './observation';
import {ObservationContainer, ObservationTypeDefinition} from './container-observation';
import {dateSeparatorSymbol, timeSeparatorSymbol} from '../application/i18n';

import * as xlsx from 'xlsx';
import {RowComponent, TabulatorFull as Tabulator} from 'tabulator-tables';

export class ObservationListVisualizer implements Visualizer {

    private inspectionContainer: ObservationContainer;
    private source: ObservationDataSource;
    private dataTable: Tabulator;
    private dataTableReady: boolean

    private isSelecting: boolean;
    private datum2WorkSheetRow: (datum: Datum) => { [key: string]: any };
    private workSheetDefinition: {
        name: string,
        titles: { [key: string]: string },
        options: {
            header: string[],
            skipHeader: boolean,
            origin: number
        }
    };

    public export: () => void;

    public constructor(private visualizerKey: string, private obsType: string, private drawer: Drawer, exportAllowed: boolean) {
        this.inspectionContainer = <ObservationContainer>this.drawer.getContainer();
        this.source = this.inspectionContainer.getDataSource();

        let tabulatorElement = <HTMLElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.list div');
        let paginationElement = <HTMLElement>this.drawer.getContainerNode().querySelector('.tlmd-list-pagination');

        this.isSelecting = false;

        //setup filter for showing deleted data as well
        this.inspectionContainer.addSubscription(
            delegatedObserver(this.inspectionContainer.getContainerNode(), 'change', `${this.drawer.getSelector()} input.show_deleted`)
                .pipe(
                    debounceTime(200))
                .subscribe(evtAndTarget => {
                    this.source.onDeletionFilterValue(this.type(), (<HTMLInputElement>evtAndTarget.target).checked ? undefined : false)
                })
        );

        //assemble all properties that are visible in the table and threfore are filterable
        let filterableProperties = [
            this.inspectionContainer.getPropertyMap()['time'],
            this.inspectionContainer.getPropertyMap()['geometry.label']
        ].concat(
            Object.entries(this.inspectionContainer.getPropertyMap())
                .filter(entry => entry[0] !== 'time' && entry[0] !== 'geometry.label' && entry[1].column.visible)
                .map(entry => entry[1])
        );

        let exportableProperties = exportAllowed
            ? [
                this.inspectionContainer.getPropertyMap()['time'],
                this.inspectionContainer.getPropertyMap()['geometry.label']
            ].concat(
                Object.entries(this.inspectionContainer.getPropertyMap())
                    .filter(entry => entry[0] !== 'time' && entry[0] !== 'geometry.label' && entry[1].column.export)
                    .map(entry => entry[1])
            )
            : [];

        let tabulatorColumns = filterableProperties.map(property => {
            if (property.column.key === 'geometry.label') {
                return Object.assign({}, property.tabulator, {width: 240})
            } else {
                return property.tabulator;
            }
        });

        const observationType = this.source.getDefinitions()[this.type()] as ObservationTypeDefinition;

        // are we supposed to have weather data?
        if (observationType?.features?.weatherFeed
            && observationType.features?.feedProbes
            && observationType.features?.addFeedToTableView === true) {

            const numToPrecision = (num: number|null, precision: number) => {
                return num ? Number(num).toFixed(precision):'';
            }

            // apply probe precision
            const sensorValueToFixedOrEmptyString = (item: ObservationDatum, probe: { precision: number, id: string }) => {
                if (item.weatherData && item.weatherData[probe.id]) {
                    return numToPrecision(item.weatherData[probe.id], probe.precision);
                }
                return '';
            }


            // only add to the table itself, don't bother making these searchable
            const sensorFeedColumns = observationType.features.feedProbes.map(probe => {
                const key = `weatherData.${probe.id}`;
                return {
                    column: {
                        key,
                        defaultContent: '-',
                        title: probe.description,
                    } as ColumnDefinition,
                    display: v => sensorValueToFixedOrEmptyString(v, probe),
                    value: v => v, // ??
                    export: v => sensorValueToFixedOrEmptyString(v as ObservationDatum, probe),
                    type: 'number',
                    tabulator: {
                        formatter: (cell) => numToPrecision(cell.getValue(), probe.precision),
                        sorter: 'number',
                        title: probe.description,
                        field: `weatherData.${probe.id}`
                    },

                } as DatumPropertyDefinition;
            });
            tabulatorColumns.push(...sensorFeedColumns.map(column => column.tabulator));
            exportableProperties.push(...sensorFeedColumns); // put it into xls as well
        } // end-if inject sensor feed data

        this.dataTableReady = false;
        this.dataTable = new Tabulator(tabulatorElement, {
            layout: 'fitDataStretch',
            layoutColumnsOnNewData: true,
            pagination: false,
            paginationMode: 'local',
            paginationElement: paginationElement,
            selectable: true,
            selectableRangeMode: 'click',
            columns: tabulatorColumns,
            initialSort: [{column: "time", dir: "desc"}]
        });
        this.dataTable.on("tableBuilt", () => {
            this.dataTableReady = true;
        });
        this.dataTable.on("rowSelectionChanged", this.propagateTableSelection.bind(this));


        // create an algorithm that finds a piece of text in all the properties of a datum and returns true is it found the text
        this.source.getData()[this.type()].setSearchFilter(
            (search: string, datum: Datum) => {
                let foundIt = search && search.length > 0 ? filterableProperties.reduce((found, propertyDef) => {
                    let text = removeDiacritics(propertyDef.display(datum).trim()).toLowerCase();
                    return found || text.indexOf(search) != -1;
                }, false) : true;
                return foundIt;
            });


        //setup free text search
        this.inspectionContainer.addSubscription(
            delegatedObserver(this.inspectionContainer.getContainerNode(), 'keydown', `${this.drawer.getSelector()} input.tlmd-list-search`)
                .pipe(
                    debounceTime(200),
                    map(evtAndTarget => removeDiacritics((<HTMLInputElement>evtAndTarget.target).value.trim()).toLowerCase()))
                .subscribe(searchText => this.source.onSearchTerm(this.type(), searchText))
        );

        //prepare for export
        this.export = exportAllowed
            ? (): void => {
                //let fileType = "ods";
                let fileType = "xlsx";
                let filename = "Ortelium_Observations-" + (new Date().toISOString());
                let workBook = xlsx.utils.book_new();
                let workSheet = this.getWorkSheet();
                xlsx.utils.book_append_sheet(workBook, workSheet.workSheet, workSheet.name);
                xlsx.writeFile(workBook, filename + '.' + fileType);
            }
            : (): void => {
            };

        this.workSheetDefinition = exportableProperties
            .map((property, index) => {
                let key = '_' + index;
                let title = property.column.title;
                return {key: key, title: title};
            })
            .reduce((workSheetDefinition, keyLabel) => {
                workSheetDefinition.options.header.push(keyLabel.key);
                workSheetDefinition.titles[keyLabel.key] = keyLabel.title;
                return workSheetDefinition;
            }, <{
                name: string,
                titles: { [key: string]: string },
                options: {
                    header: string[],
                    skipHeader: boolean,
                    origin: number,
                }
            }>{
                name: this.type(),
                titles: {},
                options: {
                    header: [],
                    skipHeader: true,
                    origin: -1,
                    cellDates: true,
                    dateNF: `yyyy${dateSeparatorSymbol}mm${dateSeparatorSymbol}dd hh${timeSeparatorSymbol}mm`
                }
            });


        this.datum2WorkSheetRow = (datum: Datum): { [key: string]: any } => {
            return exportableProperties.reduce((row, property, index) => {
                row['_' + index] = property.export(datum);
                return row;
            }, <{ [key: string]: any }>{});
        }

        //container will unsubscribe in release cycle
        this.inspectionContainer.addSubscription(
            this.drawer.getSizeObservable().pipe(
                throttleTime(300, undefined, {leading: true, trailing: true})
            ).subscribe(_drawerSize => {
                //give tabulator a change to adapt to drawer size changes
                if (this.dataTableReady) {
                    this.dataTable.redraw();
                }
            })
        )
    }

    public release(): void {
        this.releaseTabulator();
    }

    public key(): string {
        return this.visualizerKey + '_' + this.type();
    }

    public type(): string {
        return this.obsType;
    }

    public setControls(_controls: { [key: string]: VisualizerControl }): Visualizer {
        //no controls here
        return this;
    }

    public setTimeline(_timelinep: Timeline): void {
        //we do not deal with the timeline here
    }

    public setMap(_map: TLMDMap): void {
        //we do not deal with the map here
    }

    public updateSelection(): void {
        if (!this.isSelecting) {
            this.isSelecting = true;
            if (this.dataTable != null) {
                let dataList = this.source.getData()[this.type()];
                let selectionIds = dataList.getSelectionIds();
                //update selection in table
                if (this.dataTableReady) {
                    this.dataTable.deselectRow();//deselects them all
                    this.dataTable.selectRow(this.dataTable.getRows().filter(row => selectionIds.includes(row.getData().id)));
                }
            }
            this.isSelecting = false;
        }
    }

    public refresh(level: VisualizationUpdateLevel): void {
        if (this.dataTable != null) {
            if (level <= VisualizationUpdateLevel.filter) {

                let dataList = this.source.getData()[this.type()];
                let data = dataList.getData();
                if (this.dataTableReady) {
                    this.dataTable.setPageSize(data.length == 0 ? 1 : data.length);
                    //prevend the tabulator from calling this.propagateTableSelection()
                    this.isSelecting = true;
                    this.dataTable.setData(data);
                    this.isSelecting = false;
                }

            }
            if (level <= VisualizationUpdateLevel.selection) {
                //now we take care of selection ourselves
                this.updateSelection();
            }
        }
    }

    public setSearchTerm(searchTerm: string) {
        let textSearchElement = <HTMLInputElement>this.drawer.getContainerNode().querySelector('input.tlmd-list-search');
        if (textSearchElement) {
            textSearchElement.value = searchTerm ? searchTerm.trim() : '';
        }
    }

    private appendToWorkSheet(datum: Datum, workSheet: xlsx.WorkSheet): void {
        xlsx.utils.sheet_add_json(
            workSheet,
            [this.datum2WorkSheetRow(datum)],
            this.workSheetDefinition.options);
    }

    private getWorkSheet = (): { name: string, workSheet: xlsx.WorkSheet } => {
        let workSheet = xlsx.utils.json_to_sheet(
            [this.workSheetDefinition.titles],
            this.workSheetDefinition.options);

        let dataList = this.source.getData()[this.type()];
        dataList.getData().forEach(datum => {
            this.appendToWorkSheet(datum, workSheet);
        });
        return {name: this.workSheetDefinition.name, workSheet: workSheet};
    }

    private propagateTableSelection(data: Datum[], rows: RowComponent[]): void {
        if (!this.isSelecting) {
            if (!this.inspectionContainer.getDataList().isSelectionLocked()) {
                this.isSelecting = true;
                let selectionIds = (data ? data : []).map(datum => datum.id);
                this.source.onSelection(this.type(), selectionIds);
                this.isSelecting = false;
            } else {
                //selection has been locked. Nect call reverts the Tabulator selection back to what it was
                this.source.onSelection(this.type(), []);
            }
        }
    }

    private releaseTabulator(): void {
        if (this.dataTable) {
            this.dataTable.destroy();
            this.dataTable = undefined;
        }
    }

    public getAutoSelectOnDeletionId(selectionIds: string[] | string): string {
        if (selectionIds && ((<string>selectionIds).substring || ((<string[]>selectionIds).push && selectionIds.length == 1)) && this.dataTable && this.dataTableReady) {
            let id = (<string>selectionIds).substring ? <string>selectionIds : (<string[]>selectionIds)[0];
            let rows = this.dataTable.getRows().filter(row => row.getData().id == id);
            if (rows.length > 0) {
                if (rows[0].getNextRow()) {
                    return (<RowComponent>rows[0].getNextRow()).getData().id;
                } else if (rows[0].getPrevRow()) {
                    return (<RowComponent>rows[0].getPrevRow()).getData().id;
                }
            }
        }
        return undefined;
    }

}